import { useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export type SignalREvent =
  | 'OrderStatusChanged'
  | 'RequestStatusChanged'
  | 'ReportStatusChanged'
  | 'ReservationStatusChanged'
  | 'ServiceRequestStatusChanged'
  | 'SessionUpdated'
  | 'SessionRevoked'
  | 'NewMessage'
  | 'MessageReaction'
  | 'NotificationReceived'
  | 'VoucherReceived'
  | 'LaundryStatusChanged'
  | 'PhotoRequested';

type EventHandler = (data: Record<string, unknown>) => void;

/**
 * Manages a single SignalR connection to /hubs/guest.
 * Authenticates with the guest JWT from localStorage.
 * Automatically reconnects on drop.
 */
export function useSignalR() {
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const handlersRef = useRef<Map<SignalREvent, Set<EventHandler>>>(new Map());

  // Build or return existing connection
  useEffect(() => {
    const token = localStorage.getItem('gcxp_token');
    if (!token) return;

    const hubUrl = `${API_BASE_URL}/hubs/guest`;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => localStorage.getItem('gcxp_token') || '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    // Wire up all registered handlers to the live connection
    handlersRef.current.forEach((handlers, event) => {
      handlers.forEach((handler) => {
        connection.on(event, handler);
      });
    });

    connection
      .start()
      .then(() => {})
      .catch((err) => console.error('[SignalR] Connection failed:', err));

    connection.onreconnected(() => {});
    connection.onclose(() => {});

    return () => {
      connection.stop();
      connectionRef.current = null;
    };
  }, []); // single connection for the app lifetime

  /**
   * Subscribe to a hub event. Returns an unsubscribe function.
   * Safe to call before the connection is established — handlers
   * are queued and attached once the connection starts.
   */
  const on = useCallback((event: SignalREvent, handler: EventHandler) => {
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler);

    // If connection already exists, wire it up immediately
    connectionRef.current?.on(event, handler);

    return () => {
      handlersRef.current.get(event)?.delete(handler);
      connectionRef.current?.off(event, handler);
    };
  }, []);

  return { on };
}
